import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty, qtyInputStep } from '../lib/quantity';
import {
  blendingApi,
  type BlendContainerOption,
  type BlendDetails,
  type BlendListRow,
  type BlendingMeta,
} from '../services/blending';

type BlendInputRow = {
  containerId: string;
  kg: string;
};

const EMPTY_INPUT: BlendInputRow = {
  containerId: '',
  kg: '',
};

export function BlendingPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writable = canWrite('bottling');
  const currentYear = new Date().getFullYear();

  const [meta, setMeta] = useState<BlendingMeta | null>(null);
  const [rows, setRows] = useState<BlendListRow[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [details, setDetails] = useState<BlendDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [posting, setPosting] = useState<boolean>(false);

  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [createDate, setCreateDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [kind, setKind] = useState<'HONEY' | 'POLLEN'>('HONEY');
  const [siteId, setSiteId] = useState<string>('');
  const [outputProductId, setOutputProductId] = useState<string>('');
  const [outputProductQuery, setOutputProductQuery] = useState<string>('');
  const [outputStatusItemId, setOutputStatusItemId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [inputRows, setInputRows] = useState<BlendInputRow[]>([{ ...EMPTY_INPUT }, { ...EMPTY_INPUT }]);
  const [creating, setCreating] = useState<boolean>(false);

  const load = async (year: number) => {
    setLoading(true);
    try {
      const [metaRes, listRes] = await Promise.all([blendingApi.meta(), blendingApi.list(year)]);
      setMeta(metaRes);
      setRows(listRes.blends);
      setSelectedId(null);
      setDetails(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda blandningar');
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (id: number) => {
    setLoadingDetails(true);
    try {
      setDetails(await blendingApi.get(id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda blandningsdetalj');
      setDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    void load(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedId) {
      setDetails(null);
      return;
    }
    void loadDetails(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const filteredContainers = useMemo(
    () =>
      (meta?.containers ?? []).filter(
        (container) =>
          container.kind === kind &&
          (siteId === '' || container.siteId === Number(siteId))
      ),
    [kind, meta, siteId]
  );

  const statusOptions = useMemo(
    () => (kind === 'POLLEN' ? meta?.pollenStatuses ?? [] : meta?.honeyStatuses ?? []),
    [kind, meta]
  );

  const outputProducts = useMemo(
    () =>
      (meta?.products ?? []).filter((product) => {
        if (kind === 'POLLEN') {
          return product.categoryLabel === 'Pollen' || product.name.toLowerCase().includes('pollen');
        }
        return product.kind === 'BULK' || product.categoryLabel === 'Honung';
      }),
    [kind, meta]
  );

  const filteredOutputProducts = useMemo(() => {
    const term = outputProductQuery.trim().toLowerCase();
    if (!term) {
      return [];
    }
    return outputProducts.filter((product) => product.name.toLowerCase().includes(term)).slice(0, 12);
  }, [outputProductQuery, outputProducts]);

  const selectedOutputProduct = useMemo(
    () => outputProducts.find((product) => product.id === Number(outputProductId)) ?? null,
    [outputProductId, outputProducts]
  );

  const showOutputProductSuggestions = outputProductQuery.trim() !== '' && outputProductId === '';

  const totalKg = useMemo(
    () =>
      inputRows.reduce((sum, row) => {
        const next = Number(row.kg);
        return Number.isFinite(next) && next > 0 ? sum + next : sum;
      }, 0),
    [inputRows]
  );

  const resetCreate = () => {
    setCreateDate(new Date().toISOString().slice(0, 10));
    setKind('HONEY');
    setSiteId('');
    setOutputProductId('');
    setOutputProductQuery('');
    setOutputStatusItemId('');
    setNotes('');
    setInputRows([{ ...EMPTY_INPUT }, { ...EMPTY_INPUT }]);
  };

  const updateInputRow = (index: number, patch: Partial<BlendInputRow>) => {
    setInputRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    );
  };

  const getContainerById = (containerId: string): BlendContainerOption | null =>
    filteredContainers.find((container) => container.id === Number(containerId)) ?? null;

  const createBlend = async () => {
    const exactMatch =
      outputProducts.find((product) => product.name.toLowerCase() === outputProductQuery.trim().toLowerCase()) ?? null;
    const resolvedOutputProductId = Number(outputProductId || selectedOutputProduct?.id || exactMatch?.id || 0);

    if (!siteId || !resolvedOutputProductId) {
      toast.error('Välj plats och slutprodukt');
      return;
    }

    const payloadInputs = inputRows
      .filter((row) => row.containerId !== '' && row.kg.trim() !== '')
      .map((row) => ({
        containerId: Number(row.containerId),
        kg: Number(row.kg),
      }));

    if (payloadInputs.length < 2) {
      toast.error('Minst två ingående kärl krävs');
      return;
    }

    setCreating(true);
    try {
      const created = await blendingApi.create({
        blendDate: createDate,
        kind,
        siteId: Number(siteId),
        outputProductId: resolvedOutputProductId,
        outputStatusItemId: outputStatusItemId ? Number(outputStatusItemId) : null,
        notes,
        inputs: payloadInputs,
      });
      toast.success('Blandning skapad');
      setCreateOpen(false);
      resetCreate();
      await load(selectedYear);
      setSelectedId(created.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa blandning');
    } finally {
      setCreating(false);
    }
  };

  const postBlend = async () => {
    if (!details) return;
    setPosting(true);
    try {
      await blendingApi.post(details.id, {
        outputStatusItemId: details.outputStatusItemId,
      });
      toast.success('Blandning bokförd');
      await load(selectedYear);
      await loadDetails(details.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte bokföra blandning');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Blandning</h1>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((value) => value - 1)}>
              PREV
            </button>
            <span className="min-w-16 text-center text-sm font-semibold">{selectedYear}</span>
            {selectedYear < currentYear && (
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((value) => Math.min(currentYear, value + 1))}>
                NEXT
              </button>
            )}
          </div>
          {writable && (
            <button type="button" className="btn btn-primary px-2 py-1" onClick={() => setCreateOpen(true)}>
              Ny blandning
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Laddar...</p>
      ) : (
        <div className="rounded border border-gray-200 overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-2 py-1.5 text-left">Resultat</th>
                  <th className="px-2 py-1.5 text-left">Datum</th>
                  <th className="px-2 py-1.5 text-left">Typ</th>
                  <th className="px-2 py-1.5 text-left">Plats</th>
                  <th className="px-2 py-1.5 text-left">Ingående</th>
                  <th className="px-2 py-1.5 text-left">Kg</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-orange-50 ${selectedId === row.id ? 'bg-orange-50' : ''}`}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <td className="px-2 py-1.5">
                      {(formatContainerDisplayId(row.outputContainerId, row.outputContainerCode) || row.outputBatchCode) ?? `#${row.id}`}
                    </td>
                    <td className="px-2 py-1.5">{String(row.blendDate).slice(0, 10)}</td>
                    <td className="px-2 py-1.5">{row.kind === 'POLLEN' ? 'Pollen' : 'Honung'}</td>
                    <td className="px-2 py-1.5">{row.siteName}</td>
                    <td className="px-2 py-1.5">{row.inputCount}</td>
                    <td className="px-2 py-1.5">{formatQty(row.totalKg, 'kg')}</td>
                    <td className="px-2 py-1.5">{row.status === 'POSTED' ? 'Bokförd' : 'Utkast'}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-2 py-4 text-sm text-gray-500">
                      Inga blandningar för {selectedYear}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-4xl rounded border border-gray-200 bg-white p-3 space-y-3">
            <h2 className="text-base font-semibold">Ny blandning</h2>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <label className="text-sm block">
                Datum
                <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={createDate} onChange={(event) => setCreateDate(event.target.value)} />
              </label>
              <label className="text-sm block">
                Typ
                <select className="mt-1 w-full" value={kind} onChange={(event) => {
                    const nextKind = event.target.value as 'HONEY' | 'POLLEN';
                    setKind(nextKind);
                    setOutputProductId('');
                    setOutputProductQuery('');
                  }}>
                  <option value="HONEY">Honung</option>
                  <option value="POLLEN">Pollen</option>
                </select>
              </label>
              <label className="text-sm block">
                Plats
                <select className="mt-1 w-full" value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                  <option value="">Välj plats</option>
                  {(meta?.sites ?? []).map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm block">
                Resultatstatus
                <select className="mt-1 w-full" value={outputStatusItemId} onChange={(event) => setOutputStatusItemId(event.target.value)}>
                  <option value="">Ingen status</option>
                  {statusOptions.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="text-sm block">
              Slutprodukt
              <div className="mt-1">
                <input
                  type="text"
                  className="w-full"
                  value={outputProductQuery}
                  placeholder="Sök slutprodukt"
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setOutputProductQuery(nextValue);
                    const exactMatch = outputProducts.find((product) => product.name.toLowerCase() === nextValue.trim().toLowerCase());
                    setOutputProductId(exactMatch ? String(exactMatch.id) : '');
                  }}
                />
                {showOutputProductSuggestions ? (
                  <div className="mt-1 max-h-48 overflow-auto rounded border border-gray-200 bg-white">
                    {filteredOutputProducts.length > 0 ? (
                      filteredOutputProducts.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className={"block w-full px-3 py-2 text-left text-sm hover:bg-orange-50 " + (Number(outputProductId) === product.id ? "bg-orange-50 font-medium" : "")}
                          onClick={() => {
                            setOutputProductId(String(product.id));
                            setOutputProductQuery(product.name);
                          }}
                        >
                          {product.name}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">Inga träffar</div>
                    )}
                  </div>
                ) : null}
              </div>
            </label>


            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Ingående kärl</div>
                <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setInputRows((current) => [...current, { ...EMPTY_INPUT }])}>
                  Lägg till rad
                </button>
              </div>
              {inputRows.map((row, index) => {
                const selectedContainer = getContainerById(row.containerId);
                return (
                  <div key={index} className="grid grid-cols-1 gap-2 rounded border border-gray-200 p-2 md:grid-cols-[minmax(0,1fr)_180px_90px]">
                    <label className="text-sm block">
                      Kärl
                      <select
                        className="mt-1 w-full"
                        value={row.containerId}
                        onChange={(event) => {
                          const nextContainerId = event.target.value;
                          const nextContainer = getContainerById(nextContainerId);
                          updateInputRow(index, {
                            containerId: nextContainerId,
                            kg: nextContainer ? String(nextContainer.remainingKg) : '',
                          });
                        }}
                      >
                        <option value="">Välj kärl</option>
                        {filteredContainers.map((container) => (
                          <option key={container.id} value={container.id}>
                            {formatContainerDisplayId(container.id, container.containerCode)} • {container.productName} • {formatQty(container.remainingKg, 'kg')} kg kvar
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm block">
                      Kg
                      <input
                        type="number"
                        min="0"
                        step={qtyInputStep('kg')}
                        className="mt-1 w-full"
                        value={row.kg}
                        onChange={(event) => updateInputRow(index, { kg: event.target.value })}
                      />
                    </label>
                    <div className="flex items-end">
                      <button
                        type="button"
                        className="btn btn-danger px-2 py-1"
                        onClick={() => setInputRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                        disabled={inputRows.length <= 2}
                      >
                        Ta bort
                      </button>
                    </div>
                    {selectedContainer && (
                      <div className="md:col-span-3 text-xs text-gray-500">
                        Plats: {selectedContainer.siteName}
                        {selectedContainer.statusLabel ? ` • ${selectedContainer.statusLabel}` : ''}
                        {selectedContainer.waterPercent !== null
                          ? ` • Vattenhalt ${formatQty(selectedContainer.waterPercent, '%')}`
                          : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-sm text-gray-600">
              Total mängd: <span className="font-semibold">{formatQty(totalKg, 'kg')} kg</span>
            </div>

            <label className="text-sm block">
              Kommentar
              <textarea className="mt-1 w-full min-h-[70px]" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </label>

            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setCreateOpen(false)}>
                Avbryt
              </button>
              <button type="button" className="btn btn-success px-2 py-1" onClick={createBlend} disabled={creating}>
                {creating ? 'Sparar...' : 'Skapa blandning'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-4xl rounded border border-gray-200 bg-white p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">
                {(formatContainerDisplayId(details?.outputContainerId ?? null, details?.outputBatchCode ?? null) ||
                  details?.outputBatchCode) ??
                  (loadingDetails ? 'Laddar detalj...' : `Blandning #${selectedId}`)}
              </h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedId(null)}>
                Stäng
              </button>
            </div>

            {loadingDetails && <p className="text-sm text-gray-500">Laddar detalj...</p>}

            {details && !loadingDetails && (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <div>Datum: {String(details.blendDate).slice(0, 10)}</div>
                    <div>Typ: {details.kind === 'POLLEN' ? 'Pollen' : 'Honung'}</div>
                    <div>Plats: {details.siteName}</div>
                    <div>Total mängd: {formatQty(details.totalKg, 'kg')}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${details.status === 'POSTED' ? 'bg-success/15 text-success' : 'bg-orange-100 text-orange-800'}`}>
                    {details.status === 'POSTED' ? 'Bokförd' : 'Utkast'}
                  </span>
                </div>

                {details.notes && <p className="text-sm text-gray-700 whitespace-pre-wrap">{details.notes}</p>}

                <div className="space-y-1">
                  <div className="text-sm font-medium">Ingående kärl</div>
                  {details.inputs.map((input) => (
                    <div key={input.id} className="rounded border border-gray-200 px-2 py-1 text-sm">
                      <div className="font-medium">{formatContainerDisplayId(input.containerId, input.containerCode)}</div>
                      <div className="text-xs text-gray-600">
                        {input.productName} • {formatQty(input.kg, 'kg')} kg
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-1">
                  <div className="text-sm font-medium">Resultat</div>
                  {details.outputs.map((output) => (
                    <div key={output.id} className="rounded border border-gray-200 px-2 py-1 text-sm">
                      <div className="font-medium">
                        {formatContainerDisplayId(output.outputContainerId, output.outputContainerCode) ||
                          output.outputBatchCode ||
                          output.productName}
                      </div>
                      <div className="text-xs text-gray-600">
                        {output.productName} • {formatQty(output.kg, 'kg')} kg
                        {output.outputStatusLabel ? ` • ${output.outputStatusLabel}` : ''}
                      </div>
                    </div>
                  ))}
                </div>

                {writable && details.status !== 'POSTED' && (
                  <div className="flex justify-end">
                    <button type="button" className="btn btn-success px-2 py-1" onClick={postBlend} disabled={posting}>
                      {posting ? 'Bokför...' : 'Bokför blandning'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
